import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccountVoipService } from '../../../core/services/account-voip/account-voip.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { AccountVoipResource } from '../../../core/interfaces/communication.interface';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-voip-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './voip-settings.component.html',
  styleUrl: './voip-settings.component.css',
})
export class VoipSettingsComponent implements OnInit {
  lines: AccountVoipResource[] = [];
  isLoading = true;
  showForm = false;
  editingLine: AccountVoipResource | null = null;
  form!: FormGroup;
  formErrors: { [key: string]: string } = {};
  isSaving = false;

  constructor(
    private accountVoipService: AccountVoipService,
    private alertService: AlertService,
    private translate: TranslateService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadLines();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      label: ['', [Validators.required, Validators.maxLength(255)]],
      account_sid: ['', [Validators.required, Validators.pattern(/^AC[0-9a-zA-Z]{32}$/)]],
      api_key_sid: ['', [Validators.required, Validators.pattern(/^SK[0-9a-zA-Z]{32}$/)]],
      api_key_secret: [''],
      auth_token: [''],
      twiml_app_sid: ['', [Validators.required, Validators.pattern(/^AP[0-9a-zA-Z]{32}$/)]],
      caller_id: ['', [Validators.required, Validators.pattern(/^\+[1-9][0-9]{6,14}$/)]],
      intelligence_service_sid: ['', [Validators.pattern(/^GA[0-9a-zA-Z]{32}$/)]],
      agent_channel: [1],
      recording_enabled: [true],
      transcription_enabled: [true],
      is_default: [false],
    });

    // If transcription enabled, intelligence_service_sid becomes required
    this.form.get('transcription_enabled')?.valueChanges.subscribe(enabled => {
      const ctrl = this.form.get('intelligence_service_sid');
      if (enabled) {
        ctrl?.setValidators([Validators.required, Validators.pattern(/^GA[0-9a-zA-Z]{32}$/)]);
      } else {
        ctrl?.setValidators([Validators.pattern(/^GA[0-9a-zA-Z]{32}$/)]);
      }
      ctrl?.updateValueAndValidity();
    });
  }

  loadLines(): void {
    this.isLoading = true;
    this.accountVoipService.getLines({ pageSize: 100 }).subscribe({
      next: (res) => { this.lines = res.data || []; this.isLoading = false; },
      error: () => { this.isLoading = false; },
    });
  }

  openCreateForm(): void {
    this.editingLine = null;
    this.form.reset({ agent_channel: 1, recording_enabled: true, transcription_enabled: true, is_default: false });
    this.form.get('api_key_secret')?.setValidators([Validators.required]);
    this.form.get('auth_token')?.setValidators([Validators.required]);
    this.form.get('api_key_secret')?.updateValueAndValidity();
    this.form.get('auth_token')?.updateValueAndValidity();
    this.formErrors = {};
    this.showForm = true;
  }

  openEditForm(line: AccountVoipResource): void {
    this.editingLine = line;
    this.form.patchValue({
      label: line.label,
      account_sid: line.account_sid,
      api_key_sid: line.api_key_sid,
      api_key_secret: '',
      auth_token: '',
      twiml_app_sid: line.twiml_app_sid,
      caller_id: line.caller_id,
      intelligence_service_sid: line.intelligence_service_sid || '',
      agent_channel: line.agent_channel,
      recording_enabled: line.recording_enabled,
      transcription_enabled: line.transcription_enabled,
      is_default: line.is_default,
    });
    this.form.get('api_key_secret')?.clearValidators();
    this.form.get('auth_token')?.clearValidators();
    this.form.get('api_key_secret')?.updateValueAndValidity();
    this.form.get('auth_token')?.updateValueAndValidity();
    this.formErrors = {};
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
    this.editingLine = null;
  }

  save(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.isSaving = true;
    this.formErrors = {};

    const data = { ...this.form.value };
    // Remove empty secrets on edit
    if (this.editingLine) {
      if (!data.api_key_secret) delete data.api_key_secret;
      if (!data.auth_token) delete data.auth_token;
    }
    if (!data.intelligence_service_sid) data.intelligence_service_sid = null;

    const obs = this.editingLine
      ? this.accountVoipService.updateLine(this.editingLine.reference, data)
      : this.accountVoipService.createLine(data);

    obs.subscribe({
      next: (res) => {
        this.isSaving = false;
        this.alertService.success();
        this.closeForm();
        this.loadLines();
      },
      error: (err) => {
        this.isSaving = false;
        if (err.status === 422 && err.error?.errors) {
          const errors = err.error.errors;
          // Domain errors (account_voips[].msg)
          if (errors.account_voips) {
            this.alertService.error(errors.account_voips?.[0]?.msg || 'swal_messages.error_occurred');
          } else {
            // Field validation errors
            Object.keys(errors).forEach(field => {
              this.formErrors[field] = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
            });
          }
        } else {
          this.alertService.error(err.error);
        }
      },
    });
  }

  deleteLine(line: AccountVoipResource): void {
    Swal.fire({
      title: this.translate.instant('buttons.confirm_delete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('buttons.confirm'),
      cancelButtonText: this.translate.instant('buttons.cancel'),
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.accountVoipService.deleteLine(line.reference).subscribe({
        next: () => { this.alertService.success(); this.loadLines(); },
        error: (err) => this.alertService.error(err.error),
      });
    });
  }

  testConnection(line: AccountVoipResource): void {
    this.accountVoipService.testConnection(line.reference).subscribe({
      next: (res) => this.alertService.success(),
      error: (err) => this.alertService.error(err.error),
    });
  }

  syncWebhooks(line: AccountVoipResource): void {
    this.accountVoipService.syncWebhooks(line.reference).subscribe({
      next: (res) => { this.alertService.success(); this.loadLines(); },
      error: (err) => this.alertService.error(err.error),
    });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control?.invalid && control?.touched) || !!this.formErrors[field];
  }
}
